import { useState, useEffect } from "react";
import { AxiosResponse } from "axios";
import Backdrop from "components/Backdrop";
import { fetchAllEmployeesAPI, fetchEmployeePageAPI } from "api/apiHelper";
import ErrorBox from "components/ErrorBox";
import EmployeesListTable from "features/employees/EmployeesListTable";
import TabsContainer from "components/TabsContainer";
import Tab from "components/Tab";


interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

const EmployeesList = () => {
  const [employees, setEmployees] = useState<EmployeeType[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>("");
  const [filterActive, setFilterActive] = useState<boolean | null>(true);
  
  // Pagination state
  const [paginationData, setPaginationData] = useState<PaginatedResponse<EmployeeType> | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  
  // Search state
  const [searchTerm, setSearchTerm] = useState<string>("");

  const getEmployeesList = async (size: number = pageSize) => {
    setIsLoading(true);
    try {
      const response: AxiosResponse<PaginatedResponse<EmployeeType>> = await fetchAllEmployeesAPI(searchTerm, filterActive, size);
      console.log("Employees : ", response.data);
      setPaginationData(response.data);
      setEmployees(response.data.results);
      setCurrentPage(1);
      setPageSize(size); // Update page size state
      setIsLoading(false);
      setError("");
    } catch (error: any) {
      console.log(error);
      setError(error.message);
      setIsLoading(false);
    }
  };

  const getEmployeesPage = async (pageUrl: string, action: string) => {
    setIsLoading(true);
    try {
      const response: AxiosResponse<PaginatedResponse<EmployeeType>> = await fetchEmployeePageAPI(pageUrl);
      setPaginationData(response.data);
      setEmployees(response.data.results);
      setCurrentPage((prev) => (action === "next" ? prev + 1 : prev - 1));
      setIsLoading(false);
    } catch (error: any) {
      console.log(error);
      setError(error.message);
      setIsLoading(false);
    }
  };

  const handlePageSizeChange = async (newSize: number) => {
    setPageSize(newSize);
    await getEmployeesList(newSize);
  };

  const handleSearch = async () => {
    getEmployeesList();
  };

  const handleFilterChange = (filter: boolean | null) => {
    setFilterActive(filter);
  };

  useEffect(() => {
    getEmployeesList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterActive]);

  return (
    <div className="container mt-5">
      {isLoading ? (
        <Backdrop show={isLoading} />
      ) : (
        <div>
          {error && <ErrorBox errorMessage={error} />}
          
          {/* Search Bar */}
          <div className="mb-4 flex gap-2">
            <input
              type="text"
              placeholder="Search by name, email, phone, or designation..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <button
              onClick={handleSearch}
              className="px-6 py-2 bg-brand-500 text-white rounded-lg hover:bg-brand-600 dark:hover:bg-brand-500 dark:bg-brand-400"
            >
              Search
            </button>
          </div>

          <TabsContainer defaultTab={1}>
            <Tab 
              isActive={filterActive === null} 
              title="All" 
              onClick={() => handleFilterChange(null)}
            >
              <EmployeesListTable 
                tableData={employees}
                paginationData={paginationData}
                currentPage={currentPage}
                pageSize={pageSize}
                getEmployeesPage={getEmployeesPage}
                onPageSizeChange={handlePageSizeChange}
              />
            </Tab>
            <Tab 
              isActive={filterActive === true} 
              title="Active Employees" 
              onClick={() => handleFilterChange(true)}
            >
              <EmployeesListTable 
                tableData={employees}
                paginationData={paginationData}
                currentPage={currentPage}
                pageSize={pageSize}
                getEmployeesPage={getEmployeesPage}
                onPageSizeChange={handlePageSizeChange}
              />
            </Tab>
            <Tab 
              isActive={filterActive === false} 
              title="Inactive Employees" 
              onClick={() => handleFilterChange(false)}
            >
              <EmployeesListTable 
                tableData={employees}
                paginationData={paginationData}
                currentPage={currentPage}
                pageSize={pageSize}
                getEmployeesPage={getEmployeesPage}
                onPageSizeChange={handlePageSizeChange}
              />
            </Tab>
          </TabsContainer>
        </div>
      )}
    </div>
  );
};

export default EmployeesList;